import { AUTH_USER_KEY } from "../constants/storage";

const API_PREFIX = "/api/v1";

let refreshPromise = null;

const toNumberIfPossible = (value) => {
  if (value === null || value === undefined || value === "") return value;
  const next = Number(value);
  return Number.isNaN(next) ? value : next;
};

const safeParse = (value) => {
  if (!value) return null;
  try {
    return JSON.parse(value);
  } catch (_) {
    return null;
  }
};

const trimSlash = (value = "") => value.replace(/\/+$/, "");

export const unwrapApiData = (payload) => {
  if (!payload || typeof payload !== "object" || Array.isArray(payload)) {
    return payload;
  }
  if ("status" in payload && "data" in payload) {
    return payload.data;
  }
  return payload;
};

export const getNormalizedApiBaseUrl = (
  rawUrl = process.env.REACT_APP_API_URL,
) => {
  const fallback = `${window.location.origin}${API_PREFIX}`;
  const source = trimSlash(rawUrl || fallback);
  if (!source) {
    return fallback;
  }
  if (source.endsWith(API_PREFIX)) {
    return source;
  }
  if (/\/api\/v\d+$/i.test(source)) {
    return source;
  }
  return `${source}${API_PREFIX}`;
};

export const normalizeBackendFileUrl = (value) => {
  if (value === null || value === undefined) return "";
  const text = String(value).trim();
  if (!text) return "";

  const backendOrigin = trimSlash(
    getNormalizedApiBaseUrl().replace(/\/api\/v\d+\/?$/i, ""),
  );

  if (text.startsWith("/uploads/")) {
    return `${backendOrigin}${text}`;
  }

  try {
    const url = new URL(text, backendOrigin || window.location.origin);
    if (window.location.protocol === "https:" && url.protocol === "http:") {
      url.protocol = "https:";
    }
    return url.href;
  } catch (_) {
    return text;
  }
};

const normalizeRoleCode = (roleCode) => {
  switch (String(roleCode || "").toUpperCase()) {
    case "SUPER_ADMIN":
      return ["admin", "super_admin"];
    case "ADMIN":
      return ["admin"];
    case "COMMITTEE":
      return ["commite"];
    case "ASSISTANT":
      return ["assistant"];
    case "USER":
      return ["user"];
    default:
      return [];
  }
};

export const normalizeAuthUser = (payload) => {
  if (!payload) return null;

  if (
    payload.access &&
    Array.isArray(payload.role) &&
    payload.auth &&
    payload.user
  ) {
    return payload;
  }

  const data = unwrapApiData(payload);
  const user = data?.user || payload?.user || payload;
  const auth = data?.auth || payload?.auth || {};
  const profile = user?.profile || payload?.profile || null;
  const roleCode =
    auth?.role || payload?.role_code || payload?.roleCode || null;
  const permissions = auth?.permissions || payload?.permissions || [];
  const userType =
    auth?.user_type || payload?.user_type || profile?.user_type || null;
  const legacyRoles = Array.isArray(payload?.role)
    ? payload.role
    : normalizeRoleCode(roleCode);

  if (!data?.access && !payload?.access && !user?.id && !payload?.id) {
    return payload;
  }

  return {
    access: data?.access || payload?.access || null,
    refresh: data?.refresh || payload?.refresh || null,
    id: toNumberIfPossible(user?.id ?? payload?.id ?? null),
    username: user?.username || payload?.username || "",
    email: user?.email || payload?.email || "",
    phone_number: user?.phone_number || payload?.phone_number || "",
    first_name: profile?.first_name || payload?.first_name || "",
    last_name: profile?.last_name || payload?.last_name || "",
    role: legacyRoles,
    role_code: roleCode,
    permissions,
    user_type: userType,
    auth: {
      role: roleCode,
      permissions,
      user_type: userType,
    },
    user: {
      ...(typeof user === "object" && user ? user : {}),
      id: toNumberIfPossible(user?.id ?? payload?.id ?? null),
      profile,
    },
    profile,
    professional: user?.professional || payload?.professional || null,
    enterprise: user?.enterprise || payload?.enterprise || null,
    bank: user?.bank || payload?.bank || null,
    organism: user?.organism || payload?.organism || null,
  };
};

// Storage access is wrapped: Safari private mode and blocked site data make
// window.sessionStorage / localStorage throw, and a session helper must never
// take the application down with it.
const readStorage = (kind) => {
  try {
    return window[kind].getItem(AUTH_USER_KEY);
  } catch (_) {
    return null;
  }
};

const writeStorage = (kind, value) => {
  try {
    window[kind].setItem(AUTH_USER_KEY, value);
  } catch (_) {
    // Unavailable storage degrades to a session that lives for the page only.
  }
};

const removeStorage = (kind) => {
  try {
    window[kind].removeItem(AUTH_USER_KEY);
  } catch (_) {
    // Nothing to remove when storage is unavailable.
  }
};

// sessionStorage is the single store. The session therefore ends with the tab
// or window and is not shared with a new one, which is what the client asked
// for. It used to be written to localStorage as well and read from there
// first, so it survived closing the browser — and a fresh tab then mounted the
// app with localStorage full and sessionStorage empty, which the sidebar could
// not cope with (blank page until localStorage was cleared by hand).
export const getStoredAuthUser = () => {
  const session = safeParse(readStorage("sessionStorage"));
  return session ? normalizeAuthUser(session) : null;
};

export const persistAuthUser = (payload) => {
  const authUser = normalizeAuthUser(payload);
  if (!authUser) return null;
  writeStorage("sessionStorage", JSON.stringify(authUser));
  return authUser;
};

export const clearStoredAuthUser = () => {
  removeStorage("sessionStorage");
  removeStorage("localStorage");
};

// One-shot purge of the pre-fix persistent session. A browser that signed in
// before this deploy still holds a localStorage copy; nothing reads it any
// more, and leaving it would keep a token on disk for ever. Removing it costs
// that user a single re-login after the deploy. Module top level is early
// enough: helpers/api_helper.js imports this module before it reads a token.
if (typeof window !== "undefined") {
  try {
    window.localStorage.removeItem(AUTH_USER_KEY);
  } catch (_) {
    // Storage unavailable: there is nothing to purge.
  }
}

export const getAccessToken = () => getStoredAuthUser()?.access || null;

export const getRefreshToken = () => getStoredAuthUser()?.refresh || null;

const mapLegacyAbsoluteUrl = (value) => {
  if (!/^https?:\/\//i.test(value || "")) {
    return value;
  }
  try {
    const parsed = new URL(value);
    const pathname = parsed.pathname || "";
    const search = parsed.search || "";
    if (pathname.includes(API_PREFIX)) {
      return (
        pathname.slice(pathname.indexOf(API_PREFIX) + API_PREFIX.length) +
        search
      );
    }
    return `${pathname}${search}`;
  } catch (_) {
    return value;
  }
};

const normalizeGeographyPath = (path) => {
  if (/^\/countries\/?$/i.test(path)) return "/geography/countries";
  if (/^\/countries\/[^/]+\/?$/i.test(path)) {
    return path
      .replace(/^\/countries/i, "/geography/countries")
      .replace(/\/$/, "");
  }
  if (/^\/provinces\/[^/]+\/?$/i.test(path)) {
    return path
      .replace(/^\/provinces/i, "/geography/provinces")
      .replace(/\/$/, "");
  }
  if (/^\/cities\/[^/]+\/?$/i.test(path)) {
    return path.replace(/^\/cities/i, "/geography/cities").replace(/\/$/, "");
  }
  if (/^\/arrondissements\/?$/i.test(path)) return "/geography/arrondissements";
  if (/^\/arrondissements\/[^/]+/i.test(path)) {
    return path
      .replace(/^\/arrondissements/i, "/geography/arrondissements")
      .replace(/\/$/, "");
  }
  if (/^\/quartiers\/?$/i.test(path)) return "/geography/quartiers";
  if (/^\/quartiers\/[^/]+/i.test(path)) {
    return path
      .replace(/^\/quartiers/i, "/geography/quartiers")
      .replace(/\/$/, "");
  }
  return path;
};

export const normalizeRequestUrl = (value = "") => {
  const absolute = mapLegacyAbsoluteUrl(value);
  const withLeadingSlash = absolute.startsWith("/") ? absolute : `/${absolute}`;
  const withoutApiPrefix = withLeadingSlash.startsWith(API_PREFIX)
    ? withLeadingSlash.slice(API_PREFIX.length) || "/"
    : withLeadingSlash;
  const [pathname, search = ""] = withoutApiPrefix.split("?");
  let nextPath = pathname || "/";

  if (
    /^\/jwt\/create\/?$/i.test(nextPath) ||
    /^\/token\/login\/?$/i.test(nextPath)
  ) {
    nextPath = "/auth/jwt/create/";
  } else if (/^\/jwt\/refresh\/?$/i.test(nextPath)) {
    nextPath = "/auth/jwt/refresh/";
  } else if (/^\/profile\/?$/i.test(nextPath)) {
    const authUser = getStoredAuthUser();
    nextPath = authUser?.id ? `/profile/${authUser.id}/` : "/profile/";
  } else {
    nextPath = normalizeGeographyPath(nextPath);
  }

  return search ? `${nextPath}?${search}` : nextPath;
};

const transformBillingPlan = (plan) => ({
  id: plan?.id,
  designation: plan?.name || plan?.code || "",
  description: plan?.description || "",
  montant: plan?.price || 0,
  nbr_mois: plan?.durationDays ? Math.ceil(plan.durationDays / 30) : 0,
  _type: plan?.userType || "",
  // The edit screen lists the included rights from `contents`, which the
  // backend calls `entitlements`; without this the list showed up empty and
  // saving silently dropped every right the plan had.
  contents: (plan?.entitlements || []).map((code) => ({ designation: code })),
  ...plan,
});

const buildLegacySubscriptionCatalog = (plans) => {
  const items = Array.isArray(plans) ? plans : [];
  const typeIds = new Map();
  let nextTypeId = 1;

  const subscriptionTypes = [];
  items.forEach((plan) => {
    const key = String(plan?.userType || "");
    if (!key || typeIds.has(key)) return;
    typeIds.set(key, nextTypeId);
    subscriptionTypes.push({
      id: nextTypeId,
      designation: key,
    });
    nextTypeId += 1;
  });

  const subscriptions = items.map((plan) => ({
    id: plan.id,
    designation: plan.code || plan.name || `PLAN_${plan.id}`,
  }));

  return {
    subscriptions,
    subscriptionTypes,
    pricing: items.map((plan) => ({
      id: plan.id,
      subscription: plan.id,
      subscription_type: typeIds.get(String(plan?.userType || "")) || null,
      designation: plan.code || plan.name || `PLAN_${plan.id}`,
      montant: plan.price || 0,
      nbr_mois: plan.durationDays ? Math.ceil(plan.durationDays / 30) : 0,
    })),
  };
};

const filterEnterpriseLike = (items, userType) =>
  (Array.isArray(items) ? items : []).filter(
    (item) => String(item?.profile?.user_type || "").toLowerCase() === userType,
  );

const filterUsersByType = (items, userType) =>
  (Array.isArray(items) ? items : []).filter(
    (item) => String(item?.profile?.user_type || "").toLowerCase() === userType,
  );

const createSyntheticResponse = (config, data, status = 200) => ({
  data,
  status,
  statusText: "OK",
  headers: {},
  config,
  request: null,
});

const BACKEND_FETCH_TIMEOUT_MS = 20000;

const fetchFromBackend = async (path, options = {}) => {
  const controller = new AbortController();
  const timeoutId = setTimeout(
    () => controller.abort(),
    BACKEND_FETCH_TIMEOUT_MS,
  );

  let response;
  try {
    response = await fetch(
      `${getNormalizedApiBaseUrl()}${normalizeRequestUrl(path)}`,
      {
        method: options.method || "GET",
        headers: {
          ...(options.body instanceof FormData
            ? {}
            : { "Content-Type": "application/json" }),
          ...(options.headers || {}),
        },
        body:
          options.body === undefined
            ? undefined
            : options.body instanceof FormData
              ? options.body
              : JSON.stringify(options.body),
        signal: controller.signal,
      },
    );
  } catch (error) {
    if (error?.name === "AbortError") {
      throw new Error("La requete a expire, veuillez reessayer");
    }
    throw error;
  } finally {
    clearTimeout(timeoutId);
  }

  const text = await response.text();
  let payload = null;
  try {
    payload = text ? JSON.parse(text) : null;
  } catch (_) {
    payload = text;
  }

  if (!response.ok) {
    const error = new Error(
      payload?.message || payload?.detail || "Une erreur est survenue",
    );
    error.status = response.status;
    error.data = payload;
    throw error;
  }

  return unwrapApiData(payload);
};

const createSpecialAdapter = async (config) => {
  const path = normalizeRequestUrl(config.url || "");

  if (/^\/users\/professionals\/?$/i.test(path)) {
    const data = await fetchFromBackend("/users/list/", {
      headers: config.headers,
    });
    return createSyntheticResponse(
      config,
      filterUsersByType(data, "professional"),
    );
  }

  if (
    /^\/(users\/)?(enterprises|organismes|banks|entreprises|entreprise)\/?$/i.test(
      path,
    )
  ) {
    const list = await fetchFromBackend("/users/list/", {
      headers: config.headers,
    });
    const userType = path.includes("organismes")
      ? "organism"
      : path.includes("banks")
        ? "bank"
        : "enterprise";
    return createSyntheticResponse(
      config,
      filterEnterpriseLike(list, userType),
    );
  }

  if (
    /^\/(users\/)?particulars\/?$/i.test(path) ||
    /^\/particuliers\/?$/i.test(path)
  ) {
    const list = await fetchFromBackend("/users/list/", {
      headers: config.headers,
    });
    return createSyntheticResponse(
      config,
      filterUsersByType(list, "particular"),
    );
  }

  if (/^\/users\/list\/?$/i.test(path)) {
    const list = await fetchFromBackend("/users/list/", {
      headers: config.headers,
    });
    return createSyntheticResponse(config, list);
  }

  if (/^\/subscriptions\/?$/i.test(path)) {
    const plans = await fetchFromBackend("/billing/plans/", {
      headers: config.headers,
    });
    return createSyntheticResponse(
      config,
      buildLegacySubscriptionCatalog(plans).subscriptions,
    );
  }

  if (/^\/subscription-types\/?$/i.test(path)) {
    const plans = await fetchFromBackend("/billing/plans/", {
      headers: config.headers,
    });
    return createSyntheticResponse(
      config,
      buildLegacySubscriptionCatalog(plans).subscriptionTypes,
    );
  }

  if (/^\/pricing\/?$/i.test(path)) {
    const plans = await fetchFromBackend("/billing/plans/", {
      headers: config.headers,
    });
    return createSyntheticResponse(
      config,
      buildLegacySubscriptionCatalog(plans).pricing,
    );
  }

  if (
    /^\/abonnement-types\/?$/i.test(path) &&
    String(config.method || "get").toLowerCase() === "get"
  ) {
    const plans = await fetchFromBackend("/billing/plans/", {
      headers: config.headers,
    });
    return createSyntheticResponse(
      config,
      Array.isArray(plans) ? plans.map(transformBillingPlan) : [],
    );
  }

  if (
    /^\/abonnement-types\/[^/]+\/?$/i.test(path) &&
    String(config.method || "get").toLowerCase() === "get"
  ) {
    const plans = await fetchFromBackend("/billing/plans/", {
      headers: config.headers,
    });
    const planId = path.split("/").filter(Boolean).pop();
    const item = (Array.isArray(plans) ? plans : [])
      .map(transformBillingPlan)
      .find((plan) => String(plan.id) === String(planId));
    return createSyntheticResponse(config, item || null);
  }

  if (
    /^\/(contacts|deals|apps\/lead|apps\/company|tasks)\/?$/i.test(path) &&
    String(config.method || "get").toLowerCase() === "get"
  ) {
    return createSyntheticResponse(config, []);
  }

  return null;
};

const shouldSkipRefresh = (path) =>
  ["/auth/jwt/create/", "/auth/jwt/refresh/"].some((item) =>
    path.startsWith(item),
  );

/**
 * Error raised when the refresh token itself was rejected by the server.
 *
 * Only this case may destroy the stored session. A network failure, a timeout
 * or a 5xx must leave it intact: the reported bug was users being logged out
 * for good after a crash, because a transient failure at cold start wiped a
 * refresh token that was still valid for 30 days server-side.
 */
class SessionRejectedError extends Error {
  constructor(message) {
    super(message || "Session expirée");
    this.name = "SessionRejectedError";
    this.isSessionRejected = true;
  }
}

export const refreshAccessToken = async () => {
  if (refreshPromise) {
    return refreshPromise;
  }

  const refresh = getRefreshToken();
  if (!refresh) {
    clearStoredAuthUser();
    throw new SessionRejectedError("Session expirée");
  }

  refreshPromise = fetch(`${getNormalizedApiBaseUrl()}/auth/jwt/refresh/`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify({ refresh }),
  })
    .then(async (response) => {
      const raw = await response.json().catch(() => null);

      if (!response.ok) {
        // 401/403 means the refresh token is genuinely invalid or revoked.
        // Anything else (5xx, gateway error) is a server-side hiccup and must
        // not cost the user their session.
        if (response.status === 401 || response.status === 403) {
          throw new SessionRejectedError(raw?.message);
        }
        throw new Error(
          raw?.message || "Service momentanément indisponible"
        );
      }

      const data = unwrapApiData(raw);
      const currentUser = getStoredAuthUser();
      const nextUser = persistAuthUser({
        ...currentUser,
        access: data?.access || currentUser?.access,
        refresh: data?.refresh || currentUser?.refresh,
      });
      return nextUser?.access;
    })
    .catch((error) => {
      // `fetch` rejects on network errors with a TypeError; those keep the
      // session so the user can retry once connectivity is back.
      if (error?.isSessionRejected) {
        clearStoredAuthUser();
      }
      throw error;
    })
    .finally(() => {
      refreshPromise = null;
    });

  return refreshPromise;
};

const isHtmlPayload = (value) =>
  typeof value === "string" &&
  (/<\/?[a-z][\s\S]*>/i.test(value) || /<!doctype html/i.test(value));

const getErrorMessage = (error) => {
  const message = error?.response?.data?.message;
  if (message && !isHtmlPayload(message)) return message;

  const detail = error?.response?.data?.detail;
  if (detail && !isHtmlPayload(detail)) return detail;

  if (typeof error?.response?.data === "string") {
    return isHtmlPayload(error.response.data)
      ? "Le service distant a renvoye une page HTML inattendue"
      : error.response.data;
  }
  return error?.message || "Une erreur est survenue";
};

export const configureAxiosInstance = (instance, options = {}) => {
  const mode = options.mode || "response";

  instance.defaults.baseURL = getNormalizedApiBaseUrl(
    instance.defaults.baseURL,
  );

  instance.interceptors.request.use(async (config) => {
    const nextConfig = { ...config };
    nextConfig.baseURL = getNormalizedApiBaseUrl(
      nextConfig.baseURL || instance.defaults.baseURL,
    );
    nextConfig.url = normalizeRequestUrl(nextConfig.url || "");
    nextConfig.headers = nextConfig.headers || {};

    if (!nextConfig.headers.Authorization) {
      const token = getAccessToken();
      if (token) {
        nextConfig.headers.Authorization = `Bearer ${token}`;
      }
    }

    const synthetic = await createSpecialAdapter(nextConfig);
    if (synthetic) {
      nextConfig.adapter = async () => synthetic;
    }

    return nextConfig;
  });

  instance.interceptors.response.use(
    (response) => {
      const nextData = unwrapApiData(response?.data);
      if (mode === "payload") {
        return nextData;
      }
      return {
        ...response,
        data: nextData,
      };
    },
    async (error) => {
      const originalRequest = error?.config || {};
      const requestPath = normalizeRequestUrl(originalRequest.url || "");

      if (
        error?.response?.status === 401 &&
        !originalRequest._retry &&
        !shouldSkipRefresh(requestPath)
      ) {
        try {
          originalRequest._retry = true;
          const access = await refreshAccessToken();
          originalRequest.headers = originalRequest.headers || {};
          originalRequest.headers.Authorization = `Bearer ${access}`;
          const retried = await instance(originalRequest);
          return retried;
        } catch (refreshError) {
          // The refresh token itself was rejected (isSessionRejected): the
          // session is gone and clearStoredAuthUser() has already run. But
          // nothing left the page — the app stayed mounted on a stale profile
          // with every request failing, which read as a blank screen until
          // the user cleared localStorage by hand. A hard navigation resets
          // React state as well. Guarded on isSessionRejected so a network
          // error or a 5xx, which deliberately keep the session (see
          // SessionRejectedError above), never bounce anyone to the login
          // page. The language rule mirrors useAuth.logout.
          if (refreshError?.isSessionRejected && typeof window !== "undefined") {
            const here = window.location.pathname;
            const onLoginPage = here.includes("/connexion") || here.includes("/login");
            if (!onLoginPage) {
              const lang = localStorage.getItem("i18nextLng");
              window.location.assign(
                lang === "en" || lang === "En" ? "/en/login" : "/fr/connexion"
              );
            }
          }
          return Promise.reject(refreshError);
        }
      }

      const nextError = new Error(getErrorMessage(error));
      nextError.status = error?.response?.status;
      nextError.data = error?.response?.data;
      return Promise.reject(nextError);
    },
  );

  return instance;
};
